import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import type { DashboardResponse, GrantResponse, WidgetSpec } from '../../types/analytics';

const KEY = ['dashboards'];

export const useDashboards = (enabled = true) =>
  useQuery({
    queryKey: KEY,
    queryFn: async () => (await apiClient.get<DashboardResponse[]>('/dashboards')).data,
    enabled,
  });

export const useCreateDashboard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string }) =>
      (await apiClient.post<DashboardResponse>('/dashboards', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

export const useUpdateDashboard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      (await apiClient.patch<DashboardResponse>(`/dashboards/${id}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

export const useDeleteDashboard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/dashboards/${id}`); },
    onSuccess: (_data, id) => {
      qc.setQueryData<DashboardResponse[]>(KEY, (old) =>
        old?.filter((dashboard) => dashboard.id !== id));
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
};

export const useAddWidget = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dashboardId, title, spec }:
      { dashboardId: string; title: string; spec: WidgetSpec }) =>
      (await apiClient.post(`/dashboards/${dashboardId}/widgets`, { title, spec })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

export const useDeleteWidget = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dashboardId, widgetId }: { dashboardId: string; widgetId: string }) => {
      await apiClient.delete(`/dashboards/${dashboardId}/widgets/${widgetId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

export const useGrants = (dashboardId: string | null) =>
  useQuery({
    queryKey: ['dashboards', dashboardId, 'grants'],
    queryFn: async () =>
      (await apiClient.get<GrantResponse[]>(`/dashboards/${dashboardId}/grants`)).data,
    enabled: !!dashboardId,
  });

export const useAddGrant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dashboardId, body }:
      { dashboardId: string; body: { grantee_type: 'role' | 'user'; grantee_id: string; access_level: 'view' | 'edit' } }) =>
      (await apiClient.post(`/dashboards/${dashboardId}/grants`, body)).data,
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['dashboards', v.dashboardId, 'grants'] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
};

export const useDeleteGrant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dashboardId, grantId }: { dashboardId: string; grantId: string }) => {
      await apiClient.delete(`/dashboards/${dashboardId}/grants/${grantId}`);
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['dashboards', v.dashboardId, 'grants'] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
};
